import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PageContainer } from "../components/PageContainer";
import { useApi } from "../api/useApi";
import { authService } from "../services/auth.service";
import { ChangePasswordFormData, changePasswordSchema } from "../helpers/formValidation";
import { ErrorMessage } from "../components/form/ErrorMessage";
import {
  centeredPageHeaderClasses,
  pageHeadingClasses,
  pageLeadClasses,
  sectionCardClasses,
  subtleEyebrowClasses,
} from "../styles/pageClasses";
import { createJobPageClasses, loginPageClasses } from "../styles/pageSpecificClasses";
import { formBtnPrimaryClasses, formInputClasses, formLabelClasses } from "../styles/formClasses";

export default function AdminPassword() {
  const { request } = useApi();
  const [serverError, setServerError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    setServerError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      await authService.changePassword(
        {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        },
        request
      );
      reset();
      setSuccessMessage("Your password has been updated.");
    } catch (err: any) {
      setServerError(err?.message ?? "Could not update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer maxWidth="4xl">
      <div className={centeredPageHeaderClasses}>
        <span className={subtleEyebrowClasses}>Admin Security</span>
        <h1 className={`${createJobPageClasses.heading} ${pageHeadingClasses}`}>Change Password</h1>
        <p className={pageLeadClasses}>
          Update your admin password with a stronger one. Your new password must include upper and lowercase letters, a number, and a symbol.
        </p>
      </div>

      <div className={`${sectionCardClasses} mt-6`}>
        {serverError ? (
          <div className={loginPageClasses.error}>{serverError}</div>
        ) : null}

        {successMessage ? (
          <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {successMessage}
          </div>
        ) : null}

        <form onSubmit={handleSubmit(onSubmit)} className={createJobPageClasses.form}>
          <div>
            <label className={formLabelClasses}>Current Password</label>
            <input
              {...register("currentPassword")}
              type="password"
              className={formInputClasses}
              autoComplete="current-password"
              placeholder="Enter your current password"
            />
            <ErrorMessage message={errors.currentPassword?.message} />
          </div>

          <div>
            <label className={formLabelClasses}>New Password</label>
            <input
              {...register("newPassword")}
              type="password"
              className={formInputClasses}
              autoComplete="new-password"
              placeholder="Enter a stronger password"
            />
            <ErrorMessage message={errors.newPassword?.message} />
          </div>

          <div>
            <label className={formLabelClasses}>Confirm New Password</label>
            <input
              {...register("confirmPassword")}
              type="password"
              className={formInputClasses}
              autoComplete="new-password"
              placeholder="Re-enter your new password"
            />
            <ErrorMessage message={errors.confirmPassword?.message} />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={formBtnPrimaryClasses}
          >
            {loading ? "Updating..." : "Update Password"}
          </button>
        </form>
      </div>
    </PageContainer>
  );
}
